import React, { forwardRef, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, CalendarProps } from "primereact/calendar";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import { useDashlyOverlayAppendTo } from "../shared/resolveDashlyOverlayAppendTo";
import { useDashlyFieldValidation } from "../shared/useDashlyFieldValidation";
import { primeReactLocaleCode } from "../../../Util/primeReactLocale";
import type { FieldValidationRules } from "../../../Util/fieldValidation";
import "../shared/dashlyFormControl.scss";
import "./DashlyDateInput.scss";

export const DASHLY_DATE_INPUT_FORMAT = "yy-mm-dd";

export type DashlyDateInputProps = CalendarProps & {
    /** Control height scale. Default `md` (2.5rem). */
    controlSize?: DashlyControlSize;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
    /** Entity-aligned validation (required). */
    validation?: FieldValidationRules;
};

const DashlyDateInput = forwardRef<Calendar, DashlyDateInputProps>(function DashlyDateInput(
    {
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        validation,
        className,
        inputId,
        id,
        controlSize = "md",
        value,
        dateFormat = DASHLY_DATE_INPUT_FORMAT,
        locale: localeProp,
        showIcon = true,
        appendTo: appendToProp,
        panelClassName,
        onChange,
        onBlur,
        ...rest
    },
    ref,
) {
    const { i18n } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const overlayAppendTo = useDashlyOverlayAppendTo(wrapperRef);
    const resolvedAppendTo = appendToProp ?? overlayAppendTo;
    const resolvedInputId = inputId ?? id;
    const { error, invalid, markTouched } = useDashlyFieldValidation(value, validation);

    const handleChange: CalendarProps["onChange"] = (event) => {
        markTouched();
        onChange?.(event);
    };

    const handleBlur: CalendarProps["onBlur"] = (event) => {
        markTouched();
        onBlur?.(event);
    };

    const control = (
        <div
            ref={wrapperRef}
            className={classNames(
                "dashly-date-input-wrap",
                dashlyControlSizeClass(controlSize),
                invalid && "dashly-date-input-wrap--invalid",
            )}
        >
            <Calendar
                ref={ref}
                {...rest}
                inputId={resolvedInputId}
                value={value}
                dateFormat={dateFormat}
                locale={localeProp || primeReactLocaleCode(i18n.language)}
                showIcon={showIcon}
                appendTo={resolvedAppendTo}
                panelClassName={classNames("dashly-datepicker-panel", panelClassName)}
                aria-invalid={invalid || undefined}
                onChange={handleChange}
                onBlur={handleBlur}
                className={classNames(
                    "dashly-date-input",
                    "dashly-calendar",
                    "w-full",
                    invalid && "p-invalid",
                    className,
                )}
            />
        </div>
    );

    if (label || labelInfo || hint || error) {
        return (
            <DashlyFieldShell
                label={label}
                labelInfo={labelInfo}
                hint={hint}
                error={error}
                htmlFor={resolvedInputId}
                required={required ?? validation?.required}
                className={containerClassName}
            >
                {control}
            </DashlyFieldShell>
        );
    }

    if (error) {
        return (
            <div className={classNames("dashly-field-shell dashly-field-shell--invalid", containerClassName)}>
                {control}
                <span className="dashly-field-shell__error" role="alert">
                    {error}
                </span>
            </div>
        );
    }

    return control;
});

DashlyDateInput.displayName = "DashlyDateInput";

export { DashlyDateInput };
